import React, { useEffect, useMemo, useRef, useState } from "react";
import BaseButton from "Components/Base/BaseButton";
import { BaseSelect } from "Components/Base/BaseSelect";
import {
  checkStatusCodeSuccess,
  notFound,
  rupeeSymbol,
} from "Components/constants/Common";
import { useFormik } from "formik";
import { submitLabel, updateLabel } from "Components/constants/Role";
import { toast } from "react-toastify";
import { AgGridReact } from "ag-grid-react";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-quartz.css";
import { validationMessages } from "Components/constants/validation";
import * as yup from "yup";
import Loader from "Components/Base/BaseLoader";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { QuotationLabel } from "Components/constants/Quotation";
import {
  ADDROReturn,
  EDITROReturn,
  VIEWROReturn,
} from "Components/constants/ReturnRONPO";
import { viewPID } from "Api/PartIssueDetails";
import {
  addROReturn,
  editROReturn,
  viewRoReturn,
  viewSingleRoReturn,
} from "Api/ROReturn";
import {
  AMOUNT_LABEL,
  ISSUE_QTY_LABEL,
  ITEM_CODE_LABEL,
  ITEM_DESC_LABEL,
  PENDING_QTY_LABEL,
  RATE_LABEL,
  REQUESTED_QTY_LABEL,
  RETURN_QTY_LABEL,
  RETURN_QTY_MUST_BE_GREATER_ZERO,
  RO_NO_LABEL,
  SR_NO_LABEL,
} from "Components/constants/ROModule";
import { getRONumberList } from "Api/RepairOrderOpen";

const ROReturnForm = () => {
  const [loader, setLoader] = useState<boolean>(false);
  const [viewPR, setViewPR] = useState<any[]>([]);
  const [selectedRONo, setSelectedRONo] = useState<any | null>(null);
  const [btnLoader, setBtnLoader] = useState(false);
  const [RONumberList, setRONumberList] = useState<any[]>([]);
  const gridRef = useRef<AgGridReact<any>>(null);
  const { id } = useParams<{ id: string }>();
  const validationSchema = yup.object({
    RONumber: yup.string().required(validationMessages.required(RO_NO_LABEL)),
  });

  const [totalAmount, setTotalAmount] = useState<number>(0);
  const navigate = useNavigate();
  const location = useLocation();
  const isViewMode = location.pathname.includes("viewROReturn");
  const [partId, setPartId] = useState<number | null>(null);

  const getPageTitle = () => {
    if (isViewMode) return VIEWROReturn;
    if (id) return EDITROReturn;
    return ADDROReturn;
  };

  const fetchRODetailsById = (id: number) => {
    setLoader(true);
    viewPID(id)
      .then((res) => {
        if (checkStatusCodeSuccess(res?.statusCode)) {
          const data = res?.data;
          if (data && data.length > 0) {
            const pidData = data[0];
            setPartId(pidData.repair_order_id);

            const gridData = pidData.partRequisitionItem.map((item: any) => ({
              id: item.id,
              item_code: item.item_code,
              return_quantity: 0,
              parts_name: item.material_description || item.parts_name,
              qty: item.qty,
              issue_quantity: item.issue_quantity || 0,
              pending_quantity: item.pending_quantity,
              remarks: item.remarks,
              rate: item.productVariant?.rate || 0,
              amount: item.product?.amount || 0,
            }));

            setViewPR(gridData);
          }
        }
      })
      .catch((err) => {
        console.error(err);
        toast.error(err?.response?.data?.message || err?.message);
      })
      .finally(() => {
        setLoader(false);
      });
  };

  const fetchROReturnDetailsById = (id: any) => {
    setLoader(true);
    isViewMode
      ? viewSingleRoReturn(id)
          .then((res: any) => {
            if (checkStatusCodeSuccess(res?.statusCode)) {
              const data = res?.data;
              if (Array.isArray(data) && data.length > 0) {
                const pidData = data[0];
                const roNo = pidData.ro_no;
                const roId = pidData.ro_id;

                setPartId(roId);
                setSelectedRONo(roNo);

                setRONumberList((prevList) => {
                  const existingRO = prevList.find(
                    (item) => item.value === roId
                  );
                  if (!existingRO) {
                    return [
                      ...prevList,
                      {
                        label: roNo,
                        value: roId,
                      },
                    ];
                  }
                  return prevList;
                });

                formik.setFieldValue("RONumber", roId);

                const gridData = data.map((item: any) => ({
                  id: item.id,
                  item_code: item.item_code,
                  parts_name: item.item_description,
                  return_quantity: item.return_quantity,
                  amount: item.amount || 0,
                  qty: item.requested_quantity,
                  issue_quantity: item.issued_quantity || 0,
                  pending_quantity: item.pending_quantity,
                  store_location: "",
                  rate: item.rate || 0,
                  remarks: "",
                }));

                setViewPR(gridData);

                const total = gridData.reduce(
                  (sum: number, item: any) => sum + (item.amount || 0),
                  0
                );
                setTotalAmount(total);
              }
            }
          })
          .catch((err: any) => {
            console.error(err);
            toast.error(err?.response?.data?.message || err?.message);
          })
          .finally(() => {
            setLoader(false);
          })
      : viewRoReturn(id)
          .then((res: any) => {
            if (checkStatusCodeSuccess(res?.statusCode)) {
              const data = res?.data;
              if (Array.isArray(data) && data.length > 0) {
                const pidData = data[0];
                setPartId(pidData.repair_order_id);
                const roNo = pidData.repairOrder?.ro_no;
                const roId = pidData.repair_order_id;

                setSelectedRONo(roNo);
                setRONumberList((prevList) => {
                  const existingRO = prevList.find(
                    (item) => item.value === roId
                  );
                  if (!existingRO) {
                    return [
                      ...prevList,
                      {
                        label: roNo,
                        value: roId,
                      },
                    ];
                  }
                  return prevList;
                });

                formik.setFieldValue("RONumber", roId);

                const gridData = pidData.partRequisitionItem.map(
                  (item: any) => ({
                    id: item.id,
                    item_code: item.item_code,
                    parts_name: item.material_description || item.parts_name,
                    return_quantity: item.return_quantity,
                    amount: item.product?.amount || 0,
                    qty: item.qty,
                    issue_quantity: item.issue_quantity || 0,
                    pending_quantity: item.pending_quantity,
                    store_location: item.store_location,
                    rate: item.product?.rate || 0,
                    remarks: item.remarks,
                  })
                );

                setViewPR(gridData);

                const total = gridData.reduce(
                  (sum: number, item: any) => sum + (item.amount || 0),
                  0
                );
                setTotalAmount(total);
              }
            }
          })
          .catch((err: any) => {
            console.error(err);
            toast.error(err?.response?.data?.message || err?.message);
          })
          .finally(() => {
            setLoader(false);
          });
  };

  useEffect(() => {
    if (id) {
      fetchROReturnDetailsById(id);
    } else {
      setViewPR([]);
    }
  }, [id]);

  useEffect(() => {
    {
      !isViewMode &&
        getRONumberList()
          .then((res) => {
            if (checkStatusCodeSuccess(res?.statusCode)) {
              const data = res?.data;
              setRONumberList(
                data.map((item: any) => ({
                  label: item.ro_no,
                  value: item.id,
                }))
              );
            } else {
              toast.error(res.message);
            }
          })
          .catch((err) => {
            toast.error(err?.response?.data?.message || err?.message);
          });
    }
  }, []);

  const calculateTotalAmount = (data: any[]) => {
    const total = data.reduce((sum, item) => {
      const returnQty = parseFloat(item.return_quantity) || 0;
      const rate = parseFloat(item.rate) || 0;
      return sum + returnQty * rate;
    }, 0);
    setTotalAmount(total);
  };

  useEffect(() => {
    calculateTotalAmount(viewPR);
  }, [viewPR]);

  const handleReturnQuantityChange = (event: any) => {
    const { newValue, node } = event;
    const returnQty = parseFloat(newValue) || 0;

    setViewPR((prevData) => {
      const updatedData = [...prevData];
      const updatedRow = { ...updatedData[node.rowIndex] };
      updatedRow.return_quantity = returnQty;
      updatedRow.amount = returnQty * (parseFloat(updatedRow.rate) || 0);
      updatedData[node.rowIndex] = updatedRow;
      return updatedData;
    });

    gridRef.current?.api.refreshCells({
      force: true,
      columns: ["amount"],
    });
  };

  const PartRequisitionColumns = useMemo(
    () => [
      {
        headerName: SR_NO_LABEL,
        field: "srNo",
        valueGetter: "node.rowIndex + 1",
        flex: 1.5,
        minWidth: 150,
      },
      {
        headerName: ITEM_CODE_LABEL,
        field: "item_code",
        editable: false,
        flex: 1.5,
        minWidth: 150,
        sortable: true,
      },
      {
        headerName: ITEM_DESC_LABEL,
        field: "parts_name",
        editable: false,
        flex: 1.5,
        minWidth: 150,
        sortable: true,
      },
      {
        headerName: REQUESTED_QTY_LABEL,
        field: "qty",
        editable: false,
        flex: 1.5,
        minWidth: 150,
        valueParser: (params: any) => {
          const value = params.newValue;
          return value ? parseFloat(value) : 0;
        },
      },
      {
        headerName: ISSUE_QTY_LABEL,
        field: "issue_quantity",
        editable: false,
        flex: 1.5,
        minWidth: 150,
        sortable: true,
        valueParser: (params: any) => {
          const value = params.newValue;
          return value ? parseFloat(value) : 0;
        },
      },
      {
        headerName: PENDING_QTY_LABEL,
        field: "pending_quantity",
        editable: false,
        flex: 1.5,
        minWidth: 150,
        sortable: true,
        valueParser: (params: any) => {
          const value = params.newValue;
          return value ? parseFloat(value) : 0;
        },
      },
      {
        headerName: RETURN_QTY_LABEL,
        field: "return_quantity",
        headerClass: "required",
        editable: !isViewMode,
        flex: 1.5,
        minWidth: 150,
        sortable: true,
        valueParser: (params: any) => {
          const value = params.newValue;
          return value ? parseFloat(value) : 0;
        },
        onCellValueChanged: handleReturnQuantityChange,
      },
      {
        headerName: RATE_LABEL,
        field: "rate",
        editable: false,
        flex: 1.5,
        minWidth: 150,
        sortable: true,
        valueParser: (params: any) => {
          const value = params.newValue;
          return value ? parseFloat(value) : 0;
        },
      },
      {
        headerName: AMOUNT_LABEL,
        field: "amount",
        editable: false,
        flex: 1.5,
        minWidth: 150,
        sortable: true,
        valueFormatter: (params: any) => {
          const returnQty = parseFloat(params.data.return_quantity) || 0;
          const rate = parseFloat(params.data.rate) || 0;
          const amount = returnQty * rate;
          return rupeeSymbol(amount.toFixed(2));
        },
      },
    ],
    [viewPR]
  );

  const handleSubmit = async (values: any) => {
    const validRow = viewPR.some((row: any) => row.return_quantity > 0);

    if (!validRow) {
      toast.error(RETURN_QTY_MUST_BE_GREATER_ZERO);
      return;
    }
    const RoReturnDetails = viewPR.map((row: any) => ({
      id: row.id,
      item_code: row.item_code,
      item_description: row.parts_name,
      requested_quantity: row.qty || 0,
      issued_quantity: row.issue_quantity || 0,
      return_quantity: row.return_quantity || 0,
      pending_quantity: row.pending_quantity || 0,
      rate: row.rate || 0,
      amount: (row.rate || 0) * (row.return_quantity || 0),
    }));

    const payload = {
      part_id: partId,
      RoReturnDetails,
    };
    if (id) {
      setBtnLoader(true);
      editROReturn(Number(id), payload)
        .then((res: any) => {
          if (checkStatusCodeSuccess(res?.statusCode)) {
            toast.success(res?.message);
            navigate("/ROReturn");
          } else {
            toast.error(res?.message);
          }
        })
        .catch((err: any) => {
          toast.error(err?.response?.data?.message || err?.message);
        })
        .finally(() => {
          setBtnLoader(false);
        });
    } else {
      try {
        setBtnLoader(true);
        const response = await addROReturn(payload);
        if (checkStatusCodeSuccess(response?.statusCode)) {
          toast.success(response.message);
          navigate("/ROReturn");
          fetchRODetailsById(selectedRONo);
        } else {
          toast.error(response.message);
        }
      } catch (err: any) {
        toast.error(err?.response?.data?.message || err?.message);
      } finally {
        setBtnLoader(false);
      }
    }
  };

  const formik = useFormik({
    initialValues: {
      RONumber: "",
      qty: "",
    },
    validationSchema,
    onSubmit: handleSubmit,
  });

  const handleRONumberChange = (value: any) => {
    formik.setFieldValue("RONumber", value);
    setSelectedRONo(value);
    if (value) {
      fetchRODetailsById(value);
    } else {
      setViewPR([]);
    }
  };

  document.title = getPageTitle();

  return (
    <div
      className="page-content"
      style={{ cursor: isViewMode ? "not-allowed" : "auto" }}
    >
      {loader && <Loader />}
      <div className="flex-grow-1 mx-3">
        <div className="mb-1 mx-2 d-flex justify-content-between align-items-center">
          <span className="fs-4 d-flex gap-4">{getPageTitle()}</span>
          <button
            className="btn btn-danger"
            onClick={() => navigate("/ROReturn")}
          >
            <i className="mdi mdi-arrow-left" />
          </button>
        </div>
        <div className="card word-wrap">
          <div className="card-body">
            <form onSubmit={formik.handleSubmit}>
              <div className="row">
                <div className="col-md-3 col-sm-6 col-12 mb-4">
                  <BaseSelect
                    name="RONumber"
                    label={RO_NO_LABEL}
                    options={RONumberList}
                    value={formik.values.RONumber}
                    handleChange={(field: any, value: any) => {
                      handleRONumberChange(value);
                    }}
                    handleBlur={formik.handleBlur}
                    error={formik.errors.RONumber}
                    touched={formik.touched.RONumber}
                    required
                    disabled={!!id}
                  />
                </div>
              </div>

              <div className="table-css">
                <div className="ag-theme-quartz">
                  <AgGridReact
                    ref={gridRef}
                    rowData={viewPR}
                    columnDefs={PartRequisitionColumns}
                    rowSelection="multiple"
                    defaultColDef={{
                      sortable: true,
                      resizable: true,
                      flex: 1,
                      tooltipComponentParams: {
                        color: "blue",
                      },
                    }}
                    domLayout="normal"
                    getRowHeight={() => 35}
                    overlayNoRowsTemplate={notFound.dataNotFound}
                  />
                </div>
                <div className="d-flex justify-content-end align-items-start">
                  <div className="mt-4 d-flex gap-4">
                    <div className=" flex-column text-start">
                      <h6 className="mb-1 font-bold text-dark">
                        {QuotationLabel.TotalGSTAmount}
                      </h6>
                      <span className="text-center">
                        {rupeeSymbol(totalAmount.toFixed(2))}
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              <div className="d-flex justify-content-end mb-1 mt-4 w-100">
                <div className="col-12 d-flex justify-content-end mb-0">
                  {!isViewMode && (
                    <BaseButton
                      type="submit"
                      label={id ? updateLabel : submitLabel}
                      color="success"
                      loader={btnLoader}
                      disable={btnLoader}
                    />
                  )}
                </div>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ROReturnForm;
